import React, { useState, useEffect } from 'react';

import { AssetsResponse } from '@api/v4/assets/assetTypes';
import { getPortalCardData } from '@api/v4/private/portal_cards';
import RecentlyCreatedAssets from './recently_created_assets';
import RecentlyUpdatedAssets from './recently_updated_assets';
import TopScoredAssets from './top_scored_assets';
import WidgetFeaturedTaggedAssets from './featured_tagged_assets';
import WidgetForm from './widget_form';

interface Widget {
  brandHexColor: string;
  editMode: boolean;
  name: string;
  portalCardId: string;
  subType: string;
  triggerCardsRefresh: () => void;
}

export interface WidgetContextTypes {
  brandHexColor: string;
  editMode: boolean;
  name: string;
  portalCardId: string;
  subType: string;
  toggleForm: () => void;
  triggerCardsRefresh: () => void;
  widgetDataResponse: AssetsResponse;
}

const initialWidgetState: WidgetContextTypes = {
  brandHexColor: "",
  editMode: false,
  name: "",
  portalCardId: null,
  subType: null,
  toggleForm: () => {
    // do nothing
  },
  triggerCardsRefresh: () => {
    // do nothing
  },
  widgetDataResponse: undefined
};

export const WidgetsContext = React.createContext(initialWidgetState);

const subtypeMap = {
  featured_tagged_assets: {
    component: <WidgetFeaturedTaggedAssets />,
    additionalFields: "best_link_for",
    sortBy: "score",
    order: "DESC",
  },
  recently_created_assets: {
    component: <RecentlyCreatedAssets />,
    additionalFields: "best_link_for,created_at",
    sortBy: "created_at",
    order: "DESC",
  },
  recently_updated_assets: {
    component: <RecentlyUpdatedAssets />,
    additionalFields: "best_link_for,updated_at",
    sortBy: "updated_at",
    order: "DESC",
  },
  top_scored_assets: {
    component: <TopScoredAssets />,
    additionalFields: "best_link_for,score",
    sortBy: "score",
    order: "DESC",
  }
};

const Widget: React.FunctionComponent<Widget> = ({
  brandHexColor,
  editMode,
  name,
  portalCardId,
  subType,
  triggerCardsRefresh
}) => {
  const [widgetDataResponse, setWidgetDataResponse] = useState<AssetsResponse>();
  const [showForm, setShowForm] = useState(false);
  const toggleForm = (): void => setShowForm((prevState) => !prevState);
  const widgetSettings = subtypeMap[subType];

  useEffect(() => {
    const options = {
      portalCardId,
      params: {
        fields: widgetSettings.additionalFields,
        sort_by: widgetSettings.sortBy,
        order: widgetSettings.order
      }
    };
    getPortalCardData(options)
      .then((response) => {
        setWidgetDataResponse(response);
      });
  }, [portalCardId, subType]);

  const renderWidget = (): JSX.Element => (
    <WidgetsContext.Consumer>
      {(widgetContext): JSX.Element => widgetContext && widgetSettings.component}
    </WidgetsContext.Consumer>
  );

  return (
    <>
      {showForm && (
        <WidgetForm
          closeModal={toggleForm}
          portalCardId={portalCardId}
          triggerCardsRefresh={triggerCardsRefresh}
        />
      )}
      <WidgetsContext.Provider
        value={{
          brandHexColor,
          editMode,
          name,
          portalCardId,
          subType,
          toggleForm,
          triggerCardsRefresh,
          widgetDataResponse
        }}
      >
        {renderWidget()}
      </WidgetsContext.Provider>
    </>
  );
};

export default Widget;
